import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { useCallback } from 'react';
import { SESSION_TIMEOUT_KEY } from './useLoginMsal';
import clearMsalCacheKeys from './utils/clearMsalCacheKeys';
import { getIsImpersonation, msalInstance } from './msal/MsalInstance';
import {
  getLegacyConnectorClient,
  getTokenJWT,
  initializeApiFactory,
} from './api/ClientServicesCloud';
import { setDoingLogout } from './utils/doingLogoutUtils';
import { logAuthTrace } from './loginLogger';

/** Tipo di logout: esplicito (utente clicca Esci) o idle-timeout (inattività 30min) */
export type LogoutReason = 'explicit' | 'idle-timeout';

// Lock in-memory per evitare doppi logout nella stessa tab
let logoutInProgress = false;

/** Verifica se un logout è in corso (usato dal keepalive per evitare chiamate inutili) */
export const isLogoutInProgress = () => logoutInProgress;

// Assegna un ID univoco alla scheda
if (!localStorage.getItem('tabId')) {
  localStorage.setItem('tabId', Math.random().toString(36).substr(2, 9));
}

const useLogoutMSAL = () => {
  const { instance: msalInstance } = useMsal();
  const currentTabId = localStorage.getItem('tabId');

  const executeMSALLogout = useCallback(
    async (reason: LogoutReason = 'explicit') => {
      localStorage.removeItem('bluSession');
      localStorage.removeItem('bluSessionData');
      const isImpersonation = getIsImpersonation();

      logAuthTrace('executeMSALLogout:entry', { reason, isImpersonation });

      // Feature flag kill-switch: se disattivato, usa sempre logoutRedirect (comportamento pre-fix)
      const softIdleEnabled =
        localStorage.getItem('focus.auth.softIdleLogout') !== '0';

      if (reason === 'idle-timeout' && softIdleEnabled) {
        // ═══════════════════════════════════════════════════════════════
        // RAMO IDLE-TIMEOUT: cleanup locale, NO logoutRedirect.
        // Non invalida il cookie SSO di B2C → al login successivo
        // B2C può fare SSO silenzioso se il cookie è ancora valido.
        // ═══════════════════════════════════════════════════════════════
        try {
          if (msalInstance) {
            await clearMsalCacheKeys(msalInstance);
          }
        } catch {
          // clearCache fallita — il reload successivo ripartirà pulito comunque
        }

        // Notifica le altre tab (stesso meccanismo cross-tab già esistente)
        localStorage.setItem(
          'msal.logout',
          JSON.stringify({
            timestamp: Date.now(),
            tabId: currentTabId,
            expiresAt: Date.now() + 5000,
          }),
        );

        // Ricarica la pagina: LoginPage farà loginRedirect con SSO potenzialmente valido
        window.location.reload();
        return;
      }

      // ═══════════════════════════════════════════════════════════════
      // RAMO EXPLICIT: comportamento attuale invariato (logoutRedirect)
      // ═══════════════════════════════════════════════════════════════
      if (msalInstance) {
        try {
          //Registra il logout e l'ID della scheda che lo ha eseguito
          localStorage.setItem(
            'msal.logout',
            JSON.stringify({
              timestamp: Date.now(),
              tabId: currentTabId,
            }),
          );
          console.log('ENTERING LOGOUT');
          const account = msalInstance.getActiveAccount();

          await msalInstance.logoutRedirect({
            postLogoutRedirectUri: isImpersonation
              ? import.meta.env.VITE_NX_PUBLIC_REDIRECT_URI_IMPERSONATE
              : import.meta.env.VITE_NX_PUBLIC_REDIRECT_URI,
            account: account || null, // Specifica l'account attivo per il logout
          });
          // Resetta il token di accesso
        } catch (error) {
          console.error('Logout error: ', error);
          // **FORZARE logout MSAL manualmente prima del reload**
          await clearMsalCacheKeys(msalInstance);

          // Dopo un breve delay, ricarica la pagina
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      } else {
        console.error('MSAL instance is not initialized');
        await clearMsalCacheKeys(msalInstance);
        // Dopo un breve delay, ricarica la pagina
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    },
    [msalInstance, currentTabId],
  );

  /**
   * Ottiene il LegacyConnectorClient, ricreando la ApiFactory se necessario.
   * Se la factory non è inizializzata, prova a ricrearla col token disponibile
   * (dal singleton o da MSAL) per poter eseguire la logoff server-side.
   * Se non c'è nessun token disponibile, ritorna undefined.
   */
  const getOrCreateLegacyClient = useCallback(async () => {
    // Tentativo diretto: la factory potrebbe già essere inizializzata
    let client = getLegacyConnectorClient();
    if (client) return client;

    // Factory non inizializzata — provo a ricrearla col token disponibile
    try {
      let token = getTokenJWT();

      // Se il token non è nel singleton, provo ad acquisirlo da MSAL
      if (!token) {
        const account = msalInstance?.getActiveAccount();
        if (account) {
          const response = await msalInstance.acquireTokenSilent({
            scopes: [
              `https://${import.meta.env.VITE_NX_PUBLIC_YOUR_TENANT_NAME}.onmicrosoft.com/focuscloud/All`,
            ],
            account,
          });
          token = response.accessToken;
        }
      }

      if (token) {
        initializeApiFactory(token);
        client = getLegacyConnectorClient();
      }
    } catch {
      console.warn(
        '[logout] Impossibile ricreare ApiFactory per logoff server-side',
      );
    }

    return client;
  }, [msalInstance]);

  const logout = useCallback(
    async (jumpDeInit?: boolean, reason: LogoutReason = 'explicit') => {
      // Lock anti-doppio logout nella stessa tab
      if (logoutInProgress) return;
      logoutInProgress = true;

      logAuthTrace('logout:entry', { jumpDeInit, reason });
      localStorage.removeItem(SESSION_TIMEOUT_KEY);
      await setDoingLogout();
      //@ts-expect-error global variable
      window.closeSocket && window.closeSocket();

      // Per idle-timeout: la sessione server-side non è più valida (timeout 30min),
      // skip logoff per evitare race condition su connessione potenzialmente morta
      if (reason === 'idle-timeout') {
        await executeMSALLogout(reason);
        return;
      }

      // Ottieni il client, ricreando la factory se necessario
      const client = await getOrCreateLegacyClient();

      if (!client) {
        // Nessun token disponibile → nessuna sessione server-side da chiudere
        console.warn(
          '[logout] LegacyConnectorClient non disponibile, skip logoff server-side',
        );
        await executeMSALLogout(reason);
        return;
      }

      try {
        if (!jumpDeInit) {
          // Logout esplicito: attende la logoff server-side prima di procedere
          await client.logoff().catch(() => {
            /* ignora errori di logoff */
          });
          await executeMSALLogout(reason);
        } else {
          // Best-effort: prova a pulire la sessione server-side, ma non blocca il redirect.
          // Evita sessioni orfane quando il logout è triggerato da timeout inattività.
          client.logoff().catch(() => {
            /* ignora errori - il logout deve proseguire comunque */
          });
          await executeMSALLogout(reason);
        }
      } catch {
        // Fallback: se qualcosa va storto, esegui comunque il logout MSAL
        await executeMSALLogout(reason);
      }
    },
    [executeMSALLogout, getOrCreateLegacyClient],
  );

  return { logout };
};

export default useLogoutMSAL;

// Debounce per il listener di logout cross-tab
let logoutDebounceTimer: ReturnType<typeof setTimeout> | null = null;

// 🔥 **Ascoltatore globale per il logout da altre schede/app**
window.addEventListener('storage', (event) => {
  if (event.key === 'msal.logout') {
    try {
      const logoutData = JSON.parse(event.newValue ?? '{}');
      const myTabId = localStorage.getItem('tabId');

      // Reagisci al logout proveniente da un altro tab o dal front-channel.
      // Non reagire ai propri eventi (stesso tabId).
      if (
        logoutData.tabId === 'front-channel-logout' ||
        (logoutData.tabId && logoutData.tabId !== myTabId)
      ) {
        // Kill-switch: se disattivato, comportamento originale
        if (localStorage.getItem('focus.auth.v2.storageDedup') === '0') {
          clearMsalCacheKeys(msalInstance)
            .catch(() => {
              /* ignora — il reload ripartirà pulito comunque */
            })
            .then(() => {
              localStorage.removeItem('bluSession');
              localStorage.removeItem('bluSessionData');
              localStorage.removeItem('msal.logout');

              window.location.reload();
            });
          return;
        }

        // TTL: scarta eventi scaduti
        if (logoutData.expiresAt && logoutData.expiresAt < Date.now()) return;

        // Dedup: non ri-reagire nello stesso boot (sessionStorage si resetta al reload)
        if (sessionStorage.getItem('msal.logout.handled')) return;

        // Debounce 500ms: evita cascate da eventi storage multipli ravvicinati
        if (logoutDebounceTimer) clearTimeout(logoutDebounceTimer);
        logoutDebounceTimer = setTimeout(() => {
          sessionStorage.setItem('msal.logout.handled', '1');
          clearMsalCacheKeys(msalInstance)
            .catch(() => {
              /* ignora — il reload ripartirà pulito comunque */
            })
            .then(() => {
              localStorage.removeItem('bluSession');
              localStorage.removeItem('bluSessionData');
              localStorage.removeItem('msal.logout');

              window.location.reload();
            });
        }, 500);
      }
    } catch (error) {
      console.error('Errore nel parsing dei dati di logout:', error);
    }
  }

  // 🔄 **Ascoltatore per il cambio utente da un altro tab**
  // A differenza del logout completo, NON pulisce la cache MSAL
  // perché i token del nuovo utente (dal popup) devono restare.
  if (event.key === 'msal.switchUser') {
    try {
      const switchData = JSON.parse(event.newValue ?? '{}');
      const myTabId = localStorage.getItem('tabId');

      if (switchData.tabId && switchData.tabId !== myTabId) {
        localStorage.removeItem('bluSession');
        localStorage.removeItem('bluSessionData');
        localStorage.removeItem('msal.switchUser');

        window.location.reload();
      }
    } catch (error) {
      console.error('Errore nel parsing dei dati di switchUser:', error);
    }
  }
});
